// Generated by GitHub Copilot
import { Trans, useTranslation } from 'react-i18next'
import { Copy, RotateCcw, X, XCircle } from 'lucide-react'
import type CompanyInvitation from '#core/entities/auth/companies/CompanyInvitation.ts'
import type CompanyInvitationIssue from '#core/entities/auth/companies/CompanyInvitationIssue.ts'
import { SettingsTable } from './settings-table.tsx'
import { buildInvitationAcceptUrl } from './invitation-link.ts'

interface InvitationsTabProps {
  invitations: CompanyInvitation[]
  totalCount: number
  onResend: (id: string) => void
  onRevoke: (id: string) => void
  onCopyLink: (token: string) => void
  /** Most recently created/resent invitation, surfaced in the share banner (null once dismissed). */
  lastIssued: CompanyInvitationIssue | null
  onDismissLastIssued: () => void
}

/** Invitations table with status badges, row actions, and a share-link banner for the latest invite. */
export function InvitationsTab({ invitations, totalCount, onResend, onRevoke, onCopyLink, lastIssued, onDismissLastIssued }: InvitationsTabProps) {
  const { t } = useTranslation('settings')

  const inviteUrl = lastIssued ? buildInvitationAcceptUrl(lastIssued.accept_token) : ''

  return (
    <>
      {lastIssued && (
        <div className="alert alert-info d-flex flex-column gap-2 mb-3">
          <div className="d-flex align-items-start gap-2">
            <span className="flex-grow-1 small">
              <Trans
                i18nKey="usersTeams.invitationBanner.notice"
                ns="settings"
                values={{ email: lastIssued.invitation.email }}
              />
            </span>
            <button
              type="button"
              className="btn btn-sm btn-icon p-0 flex-shrink-0"
              title={t('shared.actions.close')}
              onClick={onDismissLastIssued}
            >
              <X size={16} className="text-secondary" />
            </button>
          </div>
          <div className="d-flex align-items-center gap-2">
            <input
              type="text"
              readOnly
              className="form-control form-control-sm font-monospace"
              value={inviteUrl}
              onFocus={e => e.currentTarget.select()}
            />
            <button
              type="button"
              className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1 flex-shrink-0"
              onClick={() => onCopyLink(lastIssued.accept_token)}
            >
              <Copy size={14} />
              {t('shared.fields.copyLink')}
            </button>
          </div>
          <span className="text-secondary small">
            {t('usersTeams.invitationBanner.expires', { date: lastIssued.invitation.formatDateTime(lastIssued.invitation.expires_at) })}
          </span>
        </div>
      )}

      <SettingsTable
        isEmpty={invitations.length === 0}
        emptyLabel={t('usersTeams.noInvitations')}
        countLabel={t('usersTeams.invitationsShowing', { count: invitations.length, total: totalCount })}
        headers={
          <>
            <th>{t('shared.fields.email')}</th>
            <th>{t('shared.fields.role')}</th>
            <th>{t('shared.fields.status')}</th>
            <th>{t('usersTeams.invitationTable.expires')}</th>
            <th className="text-end">{t('shared.fields.actions')}</th>
          </>
        }
      >
        {invitations.map(inv => (
          <InvitationRow
            key={inv.id}
            invitation={inv}
            onResend={() => onResend(inv.id)}
            onRevoke={() => onRevoke(inv.id)}
          />
        ))}
      </SettingsTable>
    </>
  )
}

interface InvitationRowProps {
  invitation: CompanyInvitation
  onResend: () => void
  onRevoke: () => void
}

function InvitationRow({ invitation, onResend, onRevoke }: InvitationRowProps) {
  const { t } = useTranslation('settings')

  const statusBadge = () => {
    switch (invitation.status) {
      case 'pending':
        return <span className="badge bg-warning bg-opacity-10 text-warning">{t('shared.fields.pending')}</span>
      case 'accepted':
        return <span className="badge bg-success bg-opacity-10 text-success">{t('usersTeams.invitationStatus.accepted')}</span>
      case 'revoked':
        return <span className="badge bg-danger bg-opacity-10 text-danger">{t('usersTeams.invitationStatus.revoked')}</span>
      case 'expired':
        return <span className="badge bg-secondary bg-opacity-10 text-secondary">{t('usersTeams.invitationStatus.expired')}</span>
    }
  }

  const isPending = invitation.status === 'pending'

  return (
    <tr>
      <td className="fw-medium">{invitation.email}</td>
      <td>
        {invitation.role_name && (
          <span className="badge bg-primary bg-opacity-10 text-primary">{invitation.role_name}</span>
        )}
      </td>
      <td>{statusBadge()}</td>
      <td className="text-secondary">{invitation.formatDateTime(invitation.expires_at)}</td>
      <td className="text-end">
        {isPending && (
          <>
            <button
              type="button"
              className="btn btn-sm btn-icon p-1"
              title={t('usersTeams.invitationActions.resend')}
              aria-label={t('usersTeams.invitationActions.resend')}
              onClick={onResend}
            >
              <RotateCcw size={16} className="text-secondary" />
            </button>
            <button
              type="button"
              className="btn btn-sm btn-icon p-1 ms-1"
              title={t('shared.fields.revoke')}
              onClick={onRevoke}
            >
              <XCircle size={16} className="text-danger" />
            </button>
          </>
        )}
      </td>
    </tr>
  )
}
